import React from "react";
import { useStaticQuery, graphql } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import { Link } from "gatsby";

import { mapEdgesToNodes } from "../../lib/helpers";

const LoanPrograms = ({
  heading,
  headingLevel,
  hiddenProgram,
  location,
  className,
}) => {
  const HeadingTag = headingLevel || "h2";

  const data = useStaticQuery(graphql`
    {
      loanPrograms: allSanityCityLoanPrograms(
        filter: { slug: { current: { ne: null } } }
      ) {
        edges {
          node {
            id
            city {
              title
            }
            slug {
              current
            }
            loanProgram
          }
        }
      }
    }
  `);

  const fhaNodes = (data || {}).loanPrograms
    ? mapEdgesToNodes(data.loanPrograms)
        .filter((items) => items.city.title === location)
        .filter((items) => items.loanProgram === "FHA")
    : [];

  const vaNodes = (data || {}).loanPrograms
    ? mapEdgesToNodes(data.loanPrograms)
        .filter((items) => items.city.title === location)
        .filter((items) => items.loanProgram === "VA")
    : [];

  const conventionalNodes = (data || {}).loanPrograms
    ? mapEdgesToNodes(data.loanPrograms)
        .filter((items) => items.city.title === location)
        .filter((items) => items.loanProgram === "Conventional")
    : [];

  const usdaNodes = (data || {}).loanPrograms
    ? mapEdgesToNodes(data.loanPrograms)
        .filter((items) => items.city.title === location)
        .filter((items) => items.loanProgram === "USDA")
    : [];

  const jumboNodes = (data || {}).loanPrograms
    ? mapEdgesToNodes(data.loanPrograms)
        .filter((items) => items.city.title === location)
        .filter((items) => items.loanProgram === "Jumbo")
    : [];

  return (
    <section
      className={`bg-primary-50 py-20 md:py-32 ${
        className || "mb-20 md:mb-32"
      }`}
    >
      <div className="container">
        <header className="mb-10 text-center md:mb-16">
          <HeadingTag>{heading || "Other Loan Programs"}</HeadingTag>
        </header>
        <div className="grid gap-10 md:grid-cols-2">
          <Link
            to={
              fhaNodes.length > 0
                ? fhaNodes.map((item) => `/${item.slug.current}/`)
                : "/fha-home-loans/"
            }
            className={`items-center overflow-hidden rounded-3xl bg-white p-6 text-center font-normal text-typography-body no-underline shadow-4xl hover:text-typography-body hover:shadow-5xl md:p-6 md:px-8 md:py-10 lg:flex lg:space-x-6 lg:px-10 lg:text-left ${
              hiddenProgram && hiddenProgram === 1 && "!hidden"
            }`}
          >
            <div className="mb-6 lg:mb-0">
              <StaticImage
                src="../../images/0.0 Repeating Modules/Loan Program Cards/FHA.jpg"
                loading="lazy"
                width={152}
                className="z-0 w-[96px] rounded-full md:w-[153px]"
              />
            </div>
            <div className="flex-1">
              <h3 className="heading-four mb-3">FHA</h3>
              <p className="mb-0">
                FHA loans are backed by the Federal Housing Administration and
                are especially popular with first-time home buyers.
              </p>
            </div>
          </Link>

          <Link
            to={
              vaNodes.length > 0
                ? vaNodes.map((item) => `/${item.slug.current}/`)
                : "/va-home-loans/"
            }
            className={`items-center overflow-hidden rounded-3xl bg-white p-6 text-center font-normal text-typography-body no-underline shadow-4xl hover:text-typography-body hover:shadow-5xl md:p-6 md:px-8 md:py-10 lg:flex lg:space-x-6 lg:px-10 lg:text-left ${
              hiddenProgram && hiddenProgram === 2 && "!hidden"
            }`}
          >
            <div className="mb-6 lg:mb-0">
              <StaticImage
                src="../../images/0.0 Repeating Modules/Loan Program Cards/VA.jpg"
                loading="lazy"
                width={152}
                className="z-0 w-[96px] rounded-full md:w-[153px]"
              />
            </div>
            <div className="flex-1">
              <h3 className="heading-four mb-3">VA</h3>
              <p className="mb-0">
                VA loans offer great benefits, such as low interest rates and no
                money down, for active-duty military members, veterans, or sur
                viving spouses.
              </p>
            </div>
          </Link>

          <Link
            to={
              conventionalNodes.length > 0
                ? conventionalNodes.map((item) => `/${item.slug.current}/`)
                : "/conventional-home-loans/"
            }
            className={`items-center overflow-hidden rounded-3xl bg-white p-6 text-center font-normal text-typography-body no-underline shadow-4xl hover:text-typography-body hover:shadow-5xl md:p-6 md:px-8 md:py-10 lg:flex lg:space-x-6 lg:px-10 lg:text-left ${
              hiddenProgram && hiddenProgram === 3 && "!hidden"
            }`}
          >
            <div className="mb-6 lg:mb-0">
              <StaticImage
                src="../../images/0.0 Repeating Modules/Loan Program Cards/Conventional.jpg"
                loading="lazy"
                width={152}
                className="z-0 w-[96px] rounded-full md:w-[153px]"
              />
            </div>
            <div className="flex-1">
              <h3 className="heading-four mb-3">Conventional</h3>
              <p className="mb-0">
                Private lenders offer conventional loans designed for those with
                good credit scores and a low debt-to-income (DTI) ratio.
              </p>
            </div>
          </Link>

          <Link
            to={
              usdaNodes.length > 0
                ? usdaNodes.map((item) => `/${item.slug.current}/`)
                : "/usda-home-loans/"
            }
            className={`items-center overflow-hidden rounded-3xl bg-white p-6 text-center font-normal text-typography-body no-underline shadow-4xl hover:text-typography-body hover:shadow-5xl md:p-6 md:px-8 md:py-10 lg:flex lg:space-x-6 lg:px-10 lg:text-left ${
              hiddenProgram && hiddenProgram === 4 && "!hidden"
            }`}
          >
            <div className="mb-6 lg:mb-0">
              <StaticImage
                src="../../images/0.0 Repeating Modules/Loan Program Cards/USDA.jpg"
                loading="lazy"
                width={152}
                className="z-0 w-[96px] rounded-full md:w-[153px]"
              />
            </div>
            <div className="flex-1">
              <h3 className="heading-four mb-3">USDA</h3>
              <p className="mb-0">
                USDA loans are designed for those interested in purchasing an
                affordable home that’s located in a rural area.
              </p>
            </div>
          </Link>

          <Link
            to={
              jumboNodes.length > 0
                ? jumboNodes.map((item) => `/${item.slug.current}/`)
                : "/jumbo-home-loans/"
            }
            className={`items-center overflow-hidden rounded-3xl bg-white p-6 text-center font-normal text-typography-body no-underline shadow-4xl hover:text-typography-body hover:shadow-5xl md:p-6 md:px-8 md:py-10 lg:flex lg:space-x-6 lg:px-10 lg:text-left ${
              hiddenProgram && hiddenProgram === 5 && "!hidden"
            }`}
          >
            <div className="mb-6 lg:mb-0">
              <StaticImage
                src="../../images/0.0 Repeating Modules/Loan Program Cards/Jumbo.jpg"
                loading="lazy"
                width={152}
                className="z-0 w-[96px] rounded-full md:w-[153px]"
              />
            </div>
            <div className="flex-1">
              <h3 className="heading-four mb-3">Jumbo</h3>
              <p className="mb-0">
                Some homes are priced above conventional conforming loan
                standards, and that’s where the jumbo loan comes in.
              </p>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default LoanPrograms;
